import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ExerciseForm } from "./components/ExerciseForm";
import { 
  Exercise, 
  ExerciseType,
  MultipleChoiceData,
  WriteTranslationData,
  VideoData,
  isMultipleChoiceData,
  isWriteTranslationData,
  isVideoData
} from "../exercise.interfaces";
import { useAppToast } from "@/hooks/useAppToast";
import { useExerciseContext } from "@/pages/Admin/Modules/Edit/exercises/context";

interface ExerciseDialogProps {
  onSave: (exercise: Exercise) => Promise<void>;
  dialogOpen?: boolean;
  closeDialog?: () => void;
  editingExercise?: Exercise;
}

export const ExerciseDialog: React.FC<ExerciseDialogProps> = ({
  onSave,
  dialogOpen: propDialogOpen,
  closeDialog: propCloseDialog,
  editingExercise: propEditingExercise
}) => {
  const toast = useAppToast();
  
  // Use props if provided, otherwise fall back to context
  let contextMethods = { dialogOpen: false, closeDialog: () => {}, editingExercise: undefined };
  
  try {
    if (propDialogOpen === undefined || !propCloseDialog || !propEditingExercise) {
      const context = useExerciseContext();
      contextMethods = context;
    }
  } catch (error) {
    // Context not available, use props or defaults
  }
  
  const dialogOpen = propDialogOpen !== undefined ? propDialogOpen : contextMethods.dialogOpen;
  const closeDialog = propCloseDialog || contextMethods.closeDialog;
  const editingExercise = propEditingExercise || contextMethods.editingExercise;
  
  const [exerciseData, setExerciseData] = useState<Partial<Exercise>>(
    editingExercise || { type: ExerciseType.MultipleChoice }
  );
  const [isSaving, setIsSaving] = useState(false);
  
  // Atualiza o exerciseData quando editingExercise muda
  useEffect(() => {
    if (editingExercise) {
      setExerciseData(editingExercise);
    } else {
      setExerciseData({ type: ExerciseType.MultipleChoice });
    }
  }, [editingExercise]);

  const handleExerciseChange = (data: Partial<Exercise>) => {
    // Mesclar dados e clonar profundamente opções para múltipla escolha
    const merged = { ...exerciseData, ...data, type: data.type || exerciseData.type };
    
    if (merged.data && isMultipleChoiceData(merged.data)) {
      merged.data = {
        ...merged.data,
        options: merged.data.options.map(opt => ({ ...opt })),
      };
    }

    setExerciseData(merged);
  };

  const validateExercise = (): boolean => {
    if (!exerciseData.data) {
      toast.error("Dados do exercício são obrigatórios");
      return false;
    }

    switch (exerciseData.type) {
      case ExerciseType.MultipleChoice: {
        if (!isMultipleChoiceData(exerciseData.data)) {
          toast.error("Formato de dados inválido para exercício de múltipla escolha");
          return false;
        }
        const data = exerciseData.data as MultipleChoiceData;
        
        if (!data.question?.trim()) {
          toast.error("Por favor, preencha a pergunta do exercício");
          return false;
        }
        
        if (!data.options || data.options.length < 2) {
          toast.error("O exercício deve ter pelo menos 2 opções");
          return false;
        }
        
        if (data.options.some(opt => !opt.text?.trim())) {
          toast.error("Todas as opções devem ter texto");
          return false;
        }
        
        const correctAnswers = data.options.filter(opt => opt.isCorrect);
        if (correctAnswers.length === 0) {
          toast.error("Pelo menos uma opção deve estar marcada como correta");
          return false;
        }
        break;
      }
        
      case ExerciseType.WriteTheTranslation: {
        if (!isWriteTranslationData(exerciseData.data)) {
          toast.error("Formato de dados inválido para exercício de tradução");
          return false;
        }
        const data = exerciseData.data as WriteTranslationData;
        
        if (!data.originalText?.trim()) {
          toast.error("Por favor, preencha o texto original");
          return false;
        }
        if (!data.translatedText?.trim()) {
          toast.error("Por favor, preencha a tradução");
          return false;
        }
        break;
      }
        
      case 'video':
      case 'watch_video':
      case 'watch_large_video':
      case ExerciseType.Video:
      case ExerciseType.WatchVideo:
      case ExerciseType.WatchLargeVideo: {
        if (!isVideoData(exerciseData.data)) {
          toast.error("Formato de dados inválido para exercício de vídeo");
          return false;
        }
        const data = exerciseData.data as VideoData;
        
        if (!data.title?.trim()) {
          toast.error("Por favor, preencha o título do vídeo");
          return false;
        }
        
        // Validar se há URL do vídeo nos dados tipados
        if (!data.videoUrl?.trim()) {
          toast.error("Por favor, faça o upload de um vídeo");
          return false;
        }
        break;
      }
        
      default:
        // Para outros tipos de exercício, assumir que são válidos se têm dados
        break;
    }
    
    return true;
  };

  const handleSave = async () => {
    if (!validateExercise()) {
      return;
    }

    try {
      setIsSaving(true);
      
      // Limpar a estrutura do exercício para evitar duplicação
      const cleanExercise = {
        id: exerciseData.id,
        type: exerciseData.type,
        data: exerciseData.data,
      };

      await onSave(cleanExercise as Exercise);
      
      closeDialog();
      toast.success(
        editingExercise
          ? "Exercício atualizado com sucesso"
          : "Exercício criado com sucesso"
      );
    } catch (error) {
      console.error("Erro ao salvar exercício:", error);
      toast.error("Não foi possível salvar o exercício. Tente novamente.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={dialogOpen} onOpenChange={closeDialog}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingExercise ? "Editar Exercício" : "Novo Exercício"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {editingExercise
              ? "Formulário para editar um exercício existente"
              : "Formulário para criar um novo exercício"}
          </DialogDescription>
        </DialogHeader>

        <ExerciseForm
          type={exerciseData.type as ExerciseType}
          onChange={handleExerciseChange}
          initialData={exerciseData as Exercise}
        />

        <DialogFooter>
          <Button variant="outline" onClick={closeDialog} disabled={isSaving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};